import ImageFormatter from '../../content/image-formatter/ImageFormatter';
import { LinkComponent, SubCategory } from "../../../types";
import {TileOverlays} from "../service-tiles/ServiceTiles";
import {brandedOverlayClasses, overlayClasses, popupPanelBase, tileBaseClasses} from "./serviceTile.tokens";

type ServiceTileProps = {
    service: SubCategory;
    height?: string;
    width?: string;
    isBackground?: boolean;
    Link: LinkComponent;
    variant?: TileOverlays;
    brandedOverlay?: boolean;
};


const ServiceTile: React.FC<ServiceTileProps> = ({
                                                     service,
                                                     width,
                                                     height = "170px",
                                                     Link,
                                                     variant = "default",
                                                     brandedOverlay = false,
                                                 }) => {
    if (!service) return null;

    const image = service.heroImage || service.images?.[0];
    const itemName = service.title || service.name;
    const overlayBrandColor = service.accentDarkColor || "#000000";

    const popupBg = service.accentColor || "#111827";      // neutral-900
    const popupBgDark = service.accentDarkColor || popupBg;

    const getTextColor = (hex: string) => {
        const c = hex.replace("#", "");
        const r = parseInt(c.substring(0, 2), 16);
        const g = parseInt(c.substring(2, 4), 16);
        const b = parseInt(c.substring(4, 6), 16);
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        return luminance > 0.6 ? "#000000" : "#ffffff";
    };

    return (
        <div
            className={tileBaseClasses}
            style={{ width: width || "320px", height: height }}
        >
            {/* Image */}
            <div
                className="relative w-full h-full overflow-hidden"
                style={{
                    height: variant === "overlay" || variant === "popup" ? "100%" : height,
                }}
            >
                <div className="absolute inset-0 transition-transform duration-500 group-hover:scale-105">
                    <ImageFormatter
                        src={image?.thumbnail || image?.url || image?.original}
                        alt={image?.alt || image?.originalAlt}
                        title={service.title}
                        width="100%"
                        height="100%"
                    />
                </div>

                {/* Overlay variant */}
                {variant === "overlay" && (
                    <div
                        className={brandedOverlay ? brandedOverlayClasses : overlayClasses}
                        style={
                            brandedOverlay
                                ? {
                                    backgroundImage: `linear-gradient(
                                      to top,
                                      ${overlayBrandColor} 0%,
                                      ${overlayBrandColor}CC 20%,
                                      ${overlayBrandColor}66 50%,
                                      transparent 70%
                                    )`,
                                }
                                : undefined
                        }
                    >
                        <h5 className="text-base font-medium tracking-tight line-clamp-2 drop-shadow-sm">
                            {itemName}
                        </h5>
                    </div>
                )}

                {/* Popup variant */}
                {variant === "popup" && (
                    <div
                        className={popupPanelBase}
                        style={{
                            backgroundColor: popupBg,
                        }}
                    >
                        {itemName}
                    </div>
                )}
            </div>

            {variant === "default" && (
                <div
                    className="flex flex-1 items-center justify-center px-5 py-4 text-center service-tile transition-colors"
                    style={
                        brandedOverlay
                            ? {
                                backgroundColor: service.accentDarkColor || "#111827",
                                color: getTextColor(service.accentDarkColor || "#111827"),
                            }
                            : undefined
                    }
                >
                    <h4
                        className={`text-base font-medium tracking-tight line-clamp-2 ${
                            brandedOverlay ? "" : "text-neutral-900 dark:text-neutral-100"
                        }`}
                    >
                        {itemName}
                    </h4>
                </div>
            )}

            <Link
                href={service.link}
                title={itemName}
                className="absolute inset-0 z-10"
            />
        </div>
    );
};



export default ServiceTile;
